import React, { useMemo } from 'react';

import { fcProxyApis } from '@apis/e2e/fcProxyApis';
import { TextInput } from '@components/admin/Forms';
import { Button } from '@components/Button';
import { DevLayout } from '@components/dev/DevLayout';
import { QueryContainer } from '@components/QueryContainer';
import { FCProxyAPIContent } from '@e2e/utils/FCProxyManager';
import { useMutation } from '@hooks/useMutation';
import { useQuery } from '@hooks/useQuery';
import { FCProxyPost } from '@models/FCProxy';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { isValidJSON } from '@utils/types';
import { Form, Formik } from 'formik';

const useStyles = makeMuiStyles((theme, common) => ({
  fcProxyContainer: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '1rem',
  },
  fcApiContainer: {
    ...common.flexColumn,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    padding: '0.5rem',
    rowGap: '0.5rem',
  },
}));

interface FCApiProps {
  url: keyof FCProxyAPIContent;
}

const FCApi = (props: FCApiProps) => {
  const { url } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const fcProxy = useQuery(['/e2e/fc', url], async () => {
    const response = await fcProxyApis.get('/e2e/fc/', {
      query: { fc_api: url },
    });
    return response[200]!;
  });

  const postFCProxy = useMutation(async (body: FCProxyPost) => {
    await fcProxyApis.post('/e2e/fc/', {
      body,
    });
  });

  const initialValues = useMemo(() => {
    if (fcProxy.data) {
      return {
        ...fcProxy.data,
        status_code: fcProxy.data.status_code ? String(fcProxy.data.status_code) : '',
        content: JSON.stringify(fcProxy.data.content),
      };
    }

    return undefined;
  }, [fcProxy.data]);

  return (
    <QueryContainer queries={[fcProxy]}>
      <Formik
        initialValues={initialValues!}
        validate={(values) => {
          const errors: Partial<Record<keyof typeof values, string>> = {};

          if (!isValidJSON(values.content)) {
            errors.content = 'Invalid JSON';
          }

          if (values.status_code.trim() === '') {
            errors.status_code = 'Status code cannot be empty';
          }

          if (Number.isNaN(Number.parseInt(values.status_code))) {
            errors.status_code = 'Invalid status code';
          }

          return errors;
        }}
        onSubmit={async (value) => {
          await postFCProxy.mutateAsync({
            fc_api: url,
            content: JSON.parse(value.content),
            use_original: value.use_original,
            status_code: Number(value.status_code),
          });
        }}
      >
        <Form className={classes.fcApiContainer}>
          <div style={{ marginBottom: '0.5rem' }}>{url}</div>

          <TextInput label="Status Code" name="status_code" />

          <TextInput
            label="Use Original"
            name="use_original"
            choose
            options={[
              { label: 'true', value: true },
              { label: 'false', value: false },
            ]}
          />

          <TextInput label="Content" name="content" multiline />

          <Button isLoading={postFCProxy.isLoading} color="primary" text="Update" />
        </Form>
      </Formik>
    </QueryContainer>
  );
};

export const FCProxy = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <DevLayout>
      <div className={classes.fcProxyContainer}>
        <FCApi url="/pref_and_city_by_zipcode.php" />
        <FCApi url="/prov_and_city_by_zipcode.php" />
        <FCApi url="/sim_member.php" />
      </div>
    </DevLayout>
  );
};
